<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import ProjectEmptyState from 'dashboard/routes/dashboard/projects/ProjectEmptyState.vue';

// Generic file-document list + uploader. The host injects a `documents` `api`
// adapter (get/upload/updateType/delete — each taking ownerId first) and the `ownerId`.
const props = defineProps({
  api: {
    type: Object,
    required: true,
  },
  ownerId: {
    type: [String, Number],
    required: true,
  },
});

const emit = defineEmits(['count']);

const { t } = useI18n();

// Predefined document types (custom values are stored verbatim).
const TYPE_KEYS = [
  'acta_constitutiva',
  'poder_notarial',
  'nda',
  'contrato',
  'propuesta',
  'factura',
  'identificacion',
  'otro',
];
const CUSTOM = '__custom__';

const documents = ref([]);
const isLoading = ref(false);
const isUploading = ref(false);
const isDragging = ref(false);
const fileInputRef = ref(null);
const uploadType = ref('otro');
const customType = ref('');

const typeLabel = type => {
  if (!type) return t('PROJECTS.DOCUMENTS.TYPES.otro');
  return TYPE_KEYS.includes(type)
    ? t(`PROJECTS.DOCUMENTS.TYPES.${type}`)
    : type;
};

const effectiveUploadType = () =>
  uploadType.value === CUSTOM
    ? customType.value.trim() || 'otro'
    : uploadType.value;

// Files that fulfill a document request already appear in that request's row
// (DocumentRequestsPanel), so exclude them here to avoid showing the same file twice.
const looseDocuments = computed(() =>
  documents.value.filter(doc => !doc.document_request_id)
);

const groups = computed(() => {
  const byType = looseDocuments.value.reduce((acc, doc) => {
    const key = doc.document_type || 'otro';
    (acc[key] = acc[key] || []).push(doc);
    return acc;
  }, {});
  return Object.keys(byType)
    .sort()
    .map(type => ({ type, label: typeLabel(type), items: byType[type] }));
});

const formatFileSize = bytes => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const loadDocuments = async () => {
  isLoading.value = true;
  try {
    const { data } = await props.api.get(props.ownerId);
    documents.value = data || [];
  } catch (error) {
    documents.value = [];
  } finally {
    isLoading.value = false;
    emit('count', looseDocuments.value.length);
  }
};

const uploadFiles = async files => {
  if (!files.length) return;
  isUploading.value = true;
  const type = effectiveUploadType();
  try {
    await files.reduce(
      (chain, file) =>
        chain.then(() => props.api.upload(props.ownerId, file, type)),
      Promise.resolve()
    );
    await loadDocuments();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENTS.UPLOAD_ERROR'));
  } finally {
    isUploading.value = false;
  }
};

const onFileInput = e => {
  uploadFiles(Array.from(e.target.files));
  e.target.value = '';
};

const onDrop = e => {
  e.preventDefault();
  isDragging.value = false;
  uploadFiles(Array.from(e.dataTransfer.files));
};

const onDragOver = e => {
  e.preventDefault();
  isDragging.value = true;
};

const onDragLeave = e => {
  if (e.currentTarget.contains(e.relatedTarget)) return;
  isDragging.value = false;
};

const refile = async (doc, event) => {
  const newType = event.target.value;
  try {
    await props.api.updateType(props.ownerId, doc.id, newType);
    await loadDocuments();
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENTS.REFILE_ERROR'));
  }
};

const removeDocument = async doc => {
  try {
    await props.api.delete(props.ownerId, doc.id);
    documents.value = documents.value.filter(d => d.id !== doc.id);
    emit('count', looseDocuments.value.length);
  } catch (error) {
    useAlert(t('PROJECTS.DOCUMENTS.DELETE_ERROR'));
  }
};

watch(() => props.ownerId, loadDocuments);
onMounted(loadDocuments);
</script>

<template>
  <div class="flex flex-col gap-4">
    <!-- Upload bar -->
    <div class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <span class="text-xs shrink-0 text-n-slate-10">
          {{ t('PROJECTS.DOCUMENTS.UPLOAD_AS') }}
        </span>
        <select
          v-model="uploadType"
          class="h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:outline-1 focus:outline-n-brand"
        >
          <option v-for="key in TYPE_KEYS" :key="key" :value="key">
            {{ t(`PROJECTS.DOCUMENTS.TYPES.${key}`) }}
          </option>
          <option :value="CUSTOM">{{ t('PROJECTS.DOCUMENTS.TYPE_CUSTOM') }}</option>
        </select>
        <input
          v-if="uploadType === CUSTOM"
          v-model="customType"
          type="text"
          :placeholder="t('PROJECTS.DOCUMENTS.CUSTOM_TYPE_PLACEHOLDER')"
          class="flex-1 min-w-0 h-8 px-2 text-sm border rounded-md reset-base bg-n-alpha-black2 border-n-weak text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:outline-1 focus:outline-n-brand"
        />
      </div>

      <!-- Drop zone -->
      <button
        type="button"
        class="flex flex-col items-center justify-center gap-1 px-4 py-5 text-sm text-center transition-colors border-2 border-dashed rounded-xl"
        :class="
          isDragging
            ? 'border-n-brand bg-n-alpha-1 text-n-slate-12'
            : 'border-n-weak text-n-slate-10 hover:border-n-slate-6'
        "
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
        @click="fileInputRef?.click()"
      >
        <span
          class="size-5"
          :class="
            isUploading
              ? 'i-lucide-loader-circle animate-spin'
              : 'i-lucide-cloud-upload'
          "
        />
        <span>
          {{
            isUploading
              ? t('PROJECTS.DOCUMENTS.UPLOADING')
              : t('PROJECTS.DOCUMENTS.DROP_HINT')
          }}
        </span>
        <input
          ref="fileInputRef"
          type="file"
          multiple
          class="sr-only"
          @change="onFileInput"
        />
      </button>
    </div>

    <!-- Empty -->
    <ProjectEmptyState
      v-if="!looseDocuments.length && !isLoading"
      compact
      icon="i-lucide-file-text"
      :title="t('PROJECTS.DOCUMENTS.EMPTY')"
    />

    <!-- Grouped list -->
    <div v-else class="flex flex-col gap-4">
      <div
        v-for="group in groups"
        :key="group.type"
        data-test="doc-group"
        class="flex flex-col gap-1"
      >
        <h4
          class="text-xs font-medium tracking-wide uppercase text-n-slate-10"
        >
          {{ group.label }}
        </h4>
        <ul class="flex flex-col divide-y divide-n-weak/60">
          <li
            v-for="doc in group.items"
            :key="doc.id"
            data-test="doc-row"
            class="flex items-center gap-2.5 py-2.5 group"
          >
            <span class="shrink-0 i-lucide-file-text size-4 text-n-slate-10" />
            <a
              :href="doc.file_url"
              target="_blank"
              rel="noopener noreferrer"
              class="flex-1 min-w-0 text-sm truncate text-n-slate-12 hover:text-n-blue-text"
            >
              {{ doc.filename }}
            </a>
            <span class="text-xs shrink-0 text-n-slate-10">
              {{ formatFileSize(doc.byte_size) }}
            </span>
            <select
              v-tooltip.top="t('PROJECTS.DOCUMENTS.REFILE')"
              :value="TYPE_KEYS.includes(doc.document_type) ? doc.document_type : 'otro'"
              class="h-7 px-1.5 text-xs border rounded-md reset-base shrink-0 bg-n-alpha-black2 border-n-weak text-n-slate-11 focus:outline-none focus:outline-1 focus:outline-n-brand"
              @change="refile(doc, $event)"
            >
              <option v-for="key in TYPE_KEYS" :key="key" :value="key">
                {{ t(`PROJECTS.DOCUMENTS.TYPES.${key}`) }}
              </option>
            </select>
            <Button
              v-tooltip.top="t('PROJECTS.DOCUMENTS.DELETE')"
              variant="ghost"
              color="ruby"
              size="xs"
              icon="i-lucide-x"
              class="transition-opacity opacity-0 shrink-0 group-hover:opacity-100"
              @click="removeDocument(doc)"
            />
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
